import React, { useEffect, useState } from 'react';
import { getServiceBaseUrl } from '../config/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Chip,
  Box,
  Divider,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  CircularProgress,
  Tooltip,
  Avatar,
  Snackbar,
  Alert,
} from '@mui/material';
import { jobDescriptionService } from '../services/jobDescriptionService';
import { feedbackService } from '../services/feedbackService';
import { useAuth } from '../hooks/useAuth';
import { COLORS } from '../config/colors';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fieldLabels: Record<string, string> = {
  jobTitle: 'Job Title',
  jobDescription: 'Job Description',
  jobType: 'Job Type',
  jobCategory: 'Job Category',
  department: 'Department',
  employmentLevel: 'Employment Level',
  industry: 'Industry',
  postingDate: 'Posting Date',
  closingDate: 'Closing Date',
  numberOfOpenings: 'Number of Openings',
  workLocationType: 'Work Location Type',
  country: 'Country',
  state: 'State',
  city: 'City',
  zipPostalCode: 'Zip/Postal Code',
  officeAddress: 'Office Address',
  region: 'Region',
  salaryMin: 'Salary Min',
  salaryMax: 'Salary Max',
  salaryCurrency: 'Salary Currency',
  salaryType: 'Salary Type',
  bonusEligibility: 'Bonus Eligibility',
  benefitsDescription: 'Benefits',
  minimumEducationLevel: 'Minimum Education Level',
  requiredExperienceYears: 'Required Experience (Years)',
  requiredSkills: 'Required Skills',
  preferredSkills: 'Preferred Skills',
  applicationURL: 'Application URL',
  recruiterName: 'Recruiter Name',
  recruiterEmail: 'Recruiter Email',
  companyID: 'Company ID',
  status: 'Status',
};

function formatDate(dateStr?: string) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
}

const JDDetailsDialog = ({
  jd,
  open,
  onClose,
}: {
  jd: any;
  open: boolean;
  onClose: () => void;
}) => {
  const { user } = useAuth();
  const userRole = user?.role || 'sme';
  const currentUserEmail = localStorage.getItem('user_email') || user?.email;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any | null>(null);

  // Only need JD Feedback modal - same for both HR and SME
  const [jdFeedbackModalOpen, setJDFeedbackModalOpen] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<any[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!open || !jd) return;
      setLoading(true);
      try {
        const jobId = jd.id || jd.jobId;
        const jdData = await jobDescriptionService.getById(jobId);
        if (isMounted) setDetails(jdData);
      } catch {
        if (isMounted) {
          setDetails(null);
        }
      }
      if (isMounted) setLoading(false);
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [open, jd]);

  useEffect(() => {
    if (open && jd) {
      loadFeedback();
    }
  }, [open, jd]);

  const loadFeedback = async () => {
    if (!jd) return;

    try {
      const feedback = await feedbackService.getFeedbackByEntity('job', jd.id || jd.jobId);
      setExistingFeedback(feedback);
    } catch (error) {
      console.error('Error loading feedback:', error);
    }
  };

  // Handle feedback button click - open JD feedback modal for both HR and SME
  const handleFeedbackClick = () => {
    setJDFeedbackModalOpen(true);
  };

  // Handle JD feedback submission (different endpoints for HR and SME)
  const handleJDFeedbackSubmit = async (feedbackData: any) => {
    setFeedbackLoading(true);
    try {
      console.log('Submitting JD feedback:', feedbackData);
      
      const token = localStorage.getItem('access_token');
      const jobId = feedbackData.job_id;

      const endpoint = userRole === 'hr' || userRole === 'admin' 
        ? `/feedback/hr/${jobId}` 
        : `/feedback/sme/${jobId}`;

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Feedback submitted successfully:', result);
      
      setSnackbar({
        open: true,
        message: `${userRole === 'sme' ? 'SME technical feedback' : 'HR feedback'} submitted successfully!`,
        severity: 'success',
      });
      
      setJDFeedbackModalOpen(false);
      await loadFeedback();
      
    } catch (error) {
      console.error('Error submitting JD feedback:', error);
      setSnackbar({
        open: true,
        message: `Failed to submit feedback: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (!open) return null;

  if (loading || !details) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Job Description Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              background:  COLORS.PRIMARY_BLUE_DARK,
              color: '#fff',
              '&:hover': { background: COLORS.PRIMARY_BLUE_DARK, opacity: 0.9 },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Flatten nested fields for display
  const location = details.location || {};
  const compensation = details.compensation || {};
  const requirements = details.requirements || {};
  const applicationDetails = details.applicationDetails || {};
  const company = details.company || {};

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: '#fff', color: '#222', pb: 1, borderBottom: '1px solid #f0f0f0' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
              {details.jobTitle}
            </Typography>
            {details.jobStatus && (
              <Chip
                label={details.jobStatus}
                color="default"
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  bgcolor:  COLORS.GRAY_100 ,
                  color: COLORS.PRIMARY_DARK,
                  border: '1px solid #e0e0e0',
                }}
              />
            )}
          </Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
            {details.department || '--'}
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers sx={{ background: '#fafbfc', pb: 0 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.employmentLevel}
                  </Typography>
                  <Typography>{details.employmentLevel || '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography>
                    {[location.city, location.state, location.country].filter(Boolean).join(', ') || '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.postingDate}
                  </Typography>
                  <Typography>{formatDate(details.postingDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.closingDate}
                  </Typography>
                  <Typography>{formatDate(details.closingDate)}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.salaryMin} - {fieldLabels.salaryMax}
                  </Typography>
                  <Typography>
                    {compensation?.salaryMin && compensation?.salaryMax
                      ? `${compensation?.salaryCurrency || 'USD'} ${compensation?.salaryMin} - ${compensation?.salaryMax} (${compensation?.salaryType || 'Annual'})`
                      : '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.jobType}
                  </Typography>
                  <Typography>{details.jobType || '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {fieldLabels.jobCategory}
                  </Typography>
                  <Typography>{details.jobCategory || '--'}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {fieldLabels.jobDescription}
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-line', fontSize: 15, color: '#222' }}>
              {details.jobDescription || '--'}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {fieldLabels.requiredSkills}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {Array.isArray(requirements?.requiredSkills) && requirements?.requiredSkills.length ? (
                    requirements?.requiredSkills.map((skill: string, idx: number) => (
                      <Chip
                        key={idx}
                        label={skill}
                        size="small"
                        color="default"
                        sx={{ bgcolor:  COLORS.GRAY_100 , color: COLORS.PRIMARY_DARK, fontWeight: 500 }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      --
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {fieldLabels.preferredSkills}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {Array.isArray(requirements?.preferredSkills) && requirements?.preferredSkills.length ? (
                    requirements?.preferredSkills.map((skill: string, idx: number) => (
                      <Chip
                        key={idx}
                        label={skill}
                        size="small"
                        color="default"
                        sx={{ bgcolor:  COLORS.GRAY_100 , color: '#616161', fontWeight: 500 }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      --
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {fieldLabels.applicationURL}
                </Typography>
                {applicationDetails.applicationURL ? (
                  <a
                    href={applicationDetails.applicationURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color:COLORS.PRIMARY_DARK, textDecoration: 'underline', fontWeight: 500 }}
                  >
                    {applicationDetails.applicationURL}
                  </a>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    --
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {fieldLabels.recruiterName}
                </Typography>
                <Typography>{applicationDetails.recruiterName || '--'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {fieldLabels.recruiterEmail}
                </Typography>
                <Typography>{applicationDetails.recruiterEmail || '--'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent:'flex-end', px: 3, py: 2 }}>
          {/* Right side - Close button */}
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              borderColor: 'var(--primary-blue)',
              color: 'var(--primary-blue)',
              '&:hover': { 
                background: 'var(--light-gray)',
                borderColor: 'var(--primary-blue)'
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JDDetailsDialog;